<script setup lang="ts">
import { computed, type PropType, ref, type Slots, useSlots } from "vue";

import Icon from "@/components/Icon.vue";
import { toastIconColors } from "@/themes/azerion";
import type { IconProps } from "@/types";

interface ToastPersistenceOptions {
  name: string;
  duration: number;
}

const props = defineProps({
  color: { type: String as PropType<string>, default: "green" },
  duration: {
    type: Number as PropType<number | undefined>,
    default: 5,
  },
  icon: {
    type: [String, Object] as PropType<IconProps | string | undefined>,
    default: "check_circle",
  },
  message: { type: String as PropType<string | undefined>, default: undefined },
  title: { type: String as PropType<string | undefined>, default: undefined },
  persistence: {
    type: Object as PropType<ToastPersistenceOptions | undefined>,
    default: undefined,
  },
});
const slots: Slots = useSlots();

const show = ref(true);

if (props.duration) {
  setTimeout(() => {
    show.value = false;
  }, props.duration * 1000);
}

const computedIconClasses = computed(() => ({
  "h-6 w-6": true,
  [toastIconColors[props.color]]: true,
}));

const hasTitle = computed(() => props.title || slots.title);
const hasMessage = computed(() => props.message || slots.message);

const hide = () => {
  show.value = false;
  var date = new Date();

  if (props.persistence !== undefined) {
    date.setTime(date.getTime() + 1000 * 60 * 60 * 24 * props.persistence.duration);
    const expires = `; expires=${date.toUTCString()}`;

    document.cookie = `${props.persistence.name}=1${expires}; path=/`;
  }
};

const cookieExists = (cookieName: string) => {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(cookieName)) {
      return true;
    }
  }
  return false;
};

if (props.persistence !== undefined) {
  if (cookieExists(props.persistence.name)) {
    show.value = false;
  }
}
</script>

<template>
  <transition
    enter-active-class="transform ease-out duration-300 transition"
    enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
    enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
    leave-active-class="transition ease-in duration-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="show"
      class="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5"
    >
      <div class="p-4">
        <div class="flex items-start">
          <div v-if="props.icon" class="shrink-0">
            <Icon
              v-bind="typeof props.icon === 'string' ? { name: props.icon } : props.icon"
              :class="computedIconClasses"
            />
          </div>
          <div class="ml-3 w-0 flex-1 pt-0.5">
            <p v-if="hasTitle" class="text-sm font-medium text-gray-900">
              <slot name="title">
                {{ props.title }}
              </slot>
            </p>
            <p
              v-if="hasMessage"
              :class="{
                ['text-sm text-gray-500']: true,
                ['mt-1']: hasTitle,
              }"
            >
              <slot>{{ props.message }}</slot>
            </p>
          </div>
          <div class="ml-4 flex shrink-0">
            <button
              class="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              @click="hide()"
            >
              <span class="sr-only">Close</span>
              <Icon name="clear" class="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
